import {gql} from '@apollo/client';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useRef} from 'react';

import {checkLifecycleChangeStatus} from 'benchling-apps/BenchlingAppActions';
import BenchlingAppLifecycleButton from 'benchling-apps/buttons/BenchlingAppLifecycleButton';
import {
  GetResourceFeatureAssociation,
  GetResourceFeatureAssociationVariables,
} from 'benchling-apps/interactivity/canvases/__generated/GetResourceFeatureAssociation';
import {homepageAppCanvasCreate} from 'benchling-apps/interactivity/canvases/CanvasDAO';
import {onAppsArchive, onAppsUnarchive} from 'benchling-apps/lib';
import BenchlingConfig from 'BenchlingConfig';
import {
  BenchlingAppFeatureType,
  BenchlingAppSubscriptions,
} from 'graphql-client/__generated/graphqlGlobalTypes';
import {BenchlingAppCanvasLocation} from 'graphql-client/__generated/graphqlGlobalTypes';
import {AppLifecycleState} from 'graphql-client/__generated/graphqlGlobalTypes';
import {
  BenchlingQueryResult,
  QueryResultType,
  useBenchlingQuery,
  useSkippableBenchlingQuery,
  SkippableBenchlingQueryResult,
} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import navigateToURL from 'lib/navigateToURL';
import notifications from 'notifications';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import {GRAY_LIGHT} from 'ui-library/Colors';
import {Header1, Title} from 'ui-library/Headers';
import LongTextValue from 'ui-library/LongTextValue';
import PartyList from 'ui-library/PartyList';
import TextValue from 'ui-library/TextValue';
import fuzzydate from 'util/fuzzydate';

import {GetCanvas, GetCanvasVariables} from '../interactivity/canvases/__generated/GetCanvas';
import CanvasContainer, {RESOURCE_FEATURE_ASSOCIATION_QUERY} from '../interactivity/canvases/CanvasContainer';
import {BATCHED_CANVAS_QUERY} from '../interactivity/canvases/CanvasContainer';
import {InacessibleCanvasPlaceholder} from '../interactivity/canvases/GenericCanvas/DefaultPlaceholder';
import GenericCanvasContainer from '../interactivity/canvases/GenericCanvas/GenericCanvasContainer';
import {BenchlingAdminCheckQuery} from './__generated/BenchlingAdminCheckQuery';
import {BenchlingAppDetailsGeneralBenchlingApp as BenchlingApp} from './__generated/BenchlingAppDetailsGeneralBenchlingApp';
import {getBenchlingAppDetailsQueryVariables} from './__generated/getBenchlingAppDetailsQuery';
import {getBenchlingAppLifecycleStateQuery} from './__generated/getBenchlingAppLifecycleStateQuery';
import {
  ListBenchlingAppCanvasFeatureWithLocation,
  ListBenchlingAppCanvasFeatureWithLocationVariables,
} from './__generated/ListBenchlingAppCanvasFeatureWithLocation';

export const BENCHLING_ADMIN_CHECK_QUERY = gql`
  query BenchlingAdminCheckQuery @skipAudit @engComponent(name: BENCHLING_APPS) {
    currentTenant {
      id
      permissions {
        admin
      }
    }
    currentUser {
      ... on UserNode {
        id
      }
      ... on BenchlingAppNode {
        id
      }
      ... on ServiceAccountActorNode {
        id
      }
    }
  }
`;

export const BENCHLING_APP_LIFECYCLE_STATE_QUERY = gql`
  query getBenchlingAppLifecycleStateQuery($benchlingAppId: StrictString!)
  @skipAudit
  @engComponent(name: BENCHLING_APPS) {
    benchlingApp(id: $benchlingAppId) {
      ... on BenchlingAppNode {
        id
        lifecycleState
      }
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

const NEVER_SEEN_APP_TEXT = 'App has no activity to show.';

// 5000 milliseconds = 5 seconds
const LIFECYCLE_CHANGE_RESULT_MAXIMUM_POLLING_TIME = 5000;

// 500 milliseconds = 2 seconds
const LIFECYCLE_CHANGE_POLL_FREQUENCY = 500;

const BENCHLING_APP_CANVAS_FEATURE_LISTING_QUERY = gql`
  query ListBenchlingAppCanvasFeatureWithLocation(
    $location: BenchlingAppCanvasLocation!
    $appInstallationId: StrictString!
  ) @skipAudit @engComponent(name: BENCHLING_APPS) {
    benchlingAppCanvasFeaturesForLocation(location: $location, appInstallationId: $appInstallationId) {
      canvasFeatures {
        id
        userDefinedId
        name
        type
      }
    }
  }
`;

function useHomepageCanvasData(resourceId: string): {
  featureAssocForGeneralHomeFeature: BenchlingQueryResult<
    ListBenchlingAppCanvasFeatureWithLocation,
    ListBenchlingAppCanvasFeatureWithLocationVariables
  >;
  featureAssocForLegacyHomeFeature: BenchlingQueryResult<
    GetResourceFeatureAssociation,
    GetResourceFeatureAssociationVariables
  >;
  canvasForGenericFeature: SkippableBenchlingQueryResult<GetCanvas, GetCanvasVariables>;
  canvasForLegacyHomeFeature: SkippableBenchlingQueryResult<GetCanvas, GetCanvasVariables>;
} {
  const featureAssocForGeneralHomeFeature = useBenchlingQuery<
    ListBenchlingAppCanvasFeatureWithLocation,
    ListBenchlingAppCanvasFeatureWithLocationVariables
  >(BENCHLING_APP_CANVAS_FEATURE_LISTING_QUERY, {
    variables: {
      location: BenchlingAppCanvasLocation.APP_HOME,
      appInstallationId: resourceId,
    },
  });

  const featureAssocForLegacyHomeFeature = useBenchlingQuery<
    GetResourceFeatureAssociation,
    GetResourceFeatureAssociationVariables
  >(RESOURCE_FEATURE_ASSOCIATION_QUERY, {
    variables: {
      resourceId,
      featureType: BenchlingAppFeatureType.APP_HOMEPAGE,
    },
  });

  const canvasForGenericFeature = useSkippableBenchlingQuery<GetCanvas, GetCanvasVariables>(
    BATCHED_CANVAS_QUERY,
    {
      variables: {
        resourceId,
        featureType: BenchlingAppFeatureType.CANVAS,
      },
      skip:
        featureAssocForGeneralHomeFeature.type !== QueryResultType.SUCCESS ||
        isInaccessible(featureAssocForGeneralHomeFeature.data),
    }
  );

  const canvasForLegacyHomeFeature = useSkippableBenchlingQuery<GetCanvas, GetCanvasVariables>(
    BATCHED_CANVAS_QUERY,
    {
      variables: {
        resourceId,
        featureType: BenchlingAppFeatureType.APP_HOMEPAGE,
      },
      skip:
        featureAssocForLegacyHomeFeature.type !== QueryResultType.SUCCESS ||
        isInaccessible(featureAssocForLegacyHomeFeature.data),
    }
  );

  return {
    featureAssocForGeneralHomeFeature,
    featureAssocForLegacyHomeFeature,
    canvasForGenericFeature,
    canvasForLegacyHomeFeature,
  };
}

function HomepageCanvasContainer({resourceId}: {resourceId: string}): JSX.Element {
  const {
    featureAssocForGeneralHomeFeature,
    featureAssocForLegacyHomeFeature,
    canvasForGenericFeature,
    canvasForLegacyHomeFeature,
  } = useHomepageCanvasData(resourceId);

  const doCanvasCreate = async (
    featureDeveloperDefinedId: string,
    appInstallationId: string,
    appId: string
  ): Promise<void> => {
    try {
      const response = await homepageAppCanvasCreate(appId, {
        featureDeveloperDefinedId,
        appInstallationId,
      });
      const failure = response.deliveryStatus === 'failure';
      if (failure) {
        notifications.error('Could not reach app. Please contact Benchling support if the issue persists.');
      } else {
        notifications.success('Homepage canvas created successfully!');
        featureAssocForGeneralHomeFeature.refetch?.();
        canvasForGenericFeature.refetch?.();
      }
    } catch (e) {
      notifications.notifyError(e);
      throw e;
    }
  };

  // Handle error case when exceptions like MultipleResultsFound is thrown from query
  if (
    featureAssocForGeneralHomeFeature.type === QueryResultType.ERROR ||
    featureAssocForLegacyHomeFeature.type === QueryResultType.ERROR
  ) {
    return <InacessibleCanvasPlaceholder />;
  }

  if (
    featureAssocForLegacyHomeFeature.type === QueryResultType.SUCCESS &&
    featureAssocForGeneralHomeFeature.type === QueryResultType.SUCCESS &&
    canvasForGenericFeature.type === QueryResultType.SUCCESS &&
    canvasForLegacyHomeFeature.type === QueryResultType.SUCCESS
  ) {
    if (
      !isInaccessible(featureAssocForGeneralHomeFeature.data?.benchlingAppCanvasFeaturesForLocation) &&
      featureAssocForGeneralHomeFeature.data?.benchlingAppCanvasFeaturesForLocation?.canvasFeatures?.length
    ) {
      const homepageCanvasFeature =
        featureAssocForGeneralHomeFeature.data.benchlingAppCanvasFeaturesForLocation.canvasFeatures[0];
      return (
        <GenericCanvasContainer
          resourceId={resourceId}
          appInstallationId={resourceId}
          featureUserDefinedId={homepageCanvasFeature.userDefinedId}
          canvasId={
            !isInaccessible(canvasForGenericFeature.data.benchlingAppCanvas)
              ? canvasForGenericFeature.data.benchlingAppCanvas?.id
              : undefined
          }
          doCreateCanvas={doCanvasCreate}
          forceDisable={false}
          canCreateCanvas={true}
        />
      );
    }
    // preserve same behavior when featureAssocOldSchema exists
    if (!isInaccessible(featureAssocForLegacyHomeFeature.data)) {
      return (
        <CanvasContainer
          resourceId={resourceId}
          featureType={BenchlingAppFeatureType.APP_HOMEPAGE}
          enabledOverride={true}
        />
      );
    }
  }
  return <></>;
}

type BenchlingAppDetails = Pick<
  BenchlingApp,
  | 'id'
  | 'name'
  | 'archived'
  | 'description'
  | 'lastSeen'
  | 'creator'
  | 'versionNumber'
  | 'appDefinitionId'
  | 'subscriptions'
>;
type BenchlingAppDetailsGeneralProps = BenchlingAppDetails & {refetchResults: () => void};

export default function BenchlingAppDetailsGeneral({
  id,
  name,
  archived,
  description,
  lastSeen,
  creator,
  versionNumber,
  appDefinitionId,
  refetchResults,
  subscriptions,
}: BenchlingAppDetailsGeneralProps): JSX.Element {
  return (
    <div className={classNames(css(styles.detailsGeneral))}>
      <Header1 className={classNames(css(styles.header))}>
        General
        {!archived && (
          <Tooltip title="Archive">
            <div className={classNames(css(styles.rightAlignedButton))}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="archive"
                size={ButtonSize.SMALL}
                onClick={(): void => {
                  onAppsArchive([{archived: false, id, name}], refetchResults);
                }}
              />
            </div>
          </Tooltip>
        )}
        {archived && (
          <Tooltip title="Unarchive">
            <div className={classNames(css(styles.rightAlignedButton))}>
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="unarchive"
                size={ButtonSize.SMALL}
                onClick={(): void => {
                  onAppsUnarchive([{archived: true, id, name}], refetchResults);
                }}
              />
            </div>
          </Tooltip>
        )}
        <Tooltip title="App Listing">
          <div className={classNames(css(styles.rightAlignedButton))}>
            {appDefinitionId && (
              <Button
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                icon="book"
                size={ButtonSize.SMALL}
                onClick={(): void => {
                  navigateToURL(`/app-catalog/apps/${appDefinitionId}/install`);
                }}
              />
            )}
          </div>
        </Tooltip>
      </Header1>
      <div className={classNames('u-lr-split', css(styles.detailRow))}>
        {versionNumber && (
          <div>
            <Title>Version</Title>
            <TextValue value={versionNumber} />
          </div>
        )}
        <div>
          <Title>Last Seen</Title>
          <TextValue value={lastSeen ? fuzzydate(lastSeen) : NEVER_SEEN_APP_TEXT} />
        </div>
        <div>
          <Title>Author</Title>
          <PartyList parties={[creator]} useAvatar={true} />
        </div>
      </div>
      <div className={css(styles.detailRow)}>
        <Title>Description</Title>
        <LongTextValue value={description} />
      </div>
      <br />
      <div className={css(styles.canvasContainer)}>
        {BenchlingConfig.ENABLE_HOMEPAGE_CANVAS_REIMPLEMENTED ? (
          <HomepageCanvasContainer resourceId={id} />
        ) : (
          <CanvasContainer
            resourceId={id}
            featureType={BenchlingAppFeatureType.APP_HOMEPAGE}
            enabledOverride={true}
          />
        )}
      </div>
    </div>
  );
}

export function BenchlingAppDetailsStatus({
  id,
  webhookConfigurations,
  name,
  subscriptions,
}: BenchlingApp): JSX.Element {
  const pollingStartTime = useRef<number>(0);
  const lifecycleTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const internalAdminQueryResult = useBenchlingQuery<BenchlingAdminCheckQuery>(BENCHLING_ADMIN_CHECK_QUERY);
  const lifecycleStateQueryResult = useBenchlingQuery<
    getBenchlingAppLifecycleStateQuery,
    getBenchlingAppDetailsQueryVariables
  >(BENCHLING_APP_LIFECYCLE_STATE_QUERY, {
    variables: {benchlingAppId: id},
    fetchPolicy: 'network-only',
  });
  const hasWebhookUrl = webhookConfigurations.length !== 0;

  if (
    lifecycleStateQueryResult.type === QueryResultType.PENDING ||
    internalAdminQueryResult.type === QueryResultType.PENDING
  ) {
    return (
      <BenchlingAppLifecycleButton
        appId={id}
        appLifecycleState={AppLifecycleState.INSTALLED}
        pollForChangeComplete={pollForLifecycleChangeResult}
        pollingStartTime={pollingStartTime}
        name={name}
        appActivationViewable={false}
        refetch={lifecycleStateQueryResult.refetch}
        isLoading={true}
        missingWebhookUrl={!hasWebhookUrl}
      />
    );
  }
  if (lifecycleStateQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Unable to load app. Please try again.');
    return <></>;
  }
  if (internalAdminQueryResult.type === QueryResultType.ERROR) {
    notifications.error('Unable to load current user. Please try again.');
    return <></>;
  }

  if (isInaccessible(lifecycleStateQueryResult.data.benchlingApp)) {
    return <></>;
  }

  const benchlingApp = lifecycleStateQueryResult.data.benchlingApp;
  const currentUser = internalAdminQueryResult.data.currentUser;
  const isTenantAdmin = internalAdminQueryResult.data.currentTenant.permissions.admin;
  const userIsTenantAdmin = currentUser ? isTenantAdmin : false;
  const hasActivateSubscription = subscriptions.includes(BenchlingAppSubscriptions.V2_APP_ACTIVATE_REQUESTED);
  const appActivationViewable = userIsTenantAdmin && hasActivateSubscription;

  function lifecycleTaskSuccess(currentState: AppLifecycleState): void {
    if (
      currentState === AppLifecycleState.INSTALLED ||
      currentState === AppLifecycleState.DEACTIVATED ||
      currentState === AppLifecycleState.ACTIVATE_PENDING
    ) {
      notifications.success('Successfully activated app.');
      lifecycleStateQueryResult.refetch();
    } else if (
      currentState === AppLifecycleState.ACTIVE ||
      currentState === AppLifecycleState.DEACTIVATE_PENDING
    ) {
      notifications.success('Successfully deactivated app.');
      lifecycleStateQueryResult.refetch();
    }
  }

  function lifecycleTaskFailure(currentState: AppLifecycleState): void {
    if (
      currentState === AppLifecycleState.INSTALLED ||
      currentState === AppLifecycleState.DEACTIVATED ||
      currentState === AppLifecycleState.ACTIVATE_PENDING
    ) {
      notifications.error(
        'Failed to activate app. Please try again or contact Benchling Support if the issue persists.'
      );
      lifecycleStateQueryResult.refetch();
    } else if (
      currentState === AppLifecycleState.ACTIVE ||
      currentState === AppLifecycleState.DEACTIVATE_PENDING
    ) {
      notifications.error(
        'An error occurred while deactivating the app. ' +
          'The app is deactivated, but the app developer may not have been notified.'
      );
      lifecycleStateQueryResult.refetch();
    }
  }

  function lifecycleTaskTimeout(currentState: AppLifecycleState): void {
    if (
      currentState === AppLifecycleState.INSTALLED ||
      currentState === AppLifecycleState.DEACTIVATED ||
      currentState === AppLifecycleState.ACTIVATE_PENDING
    ) {
      notifications.error(
        'activate request timed out. Please try again or contact Benchling Support if the issue persists.'
      );
      lifecycleStateQueryResult.refetch();
    } else if (
      currentState === AppLifecycleState.ACTIVE ||
      currentState === AppLifecycleState.DEACTIVATE_PENDING
    ) {
      notifications.error(
        'deactivate request timed out. Please try again or contact Benchling Support if the issue persists.'
      );
      lifecycleStateQueryResult.refetch();
    }
  }

  async function pollForLifecycleChangeResult(statusUrl: string): Promise<void> {
    const lifecycleChangeStatus = await checkLifecycleChangeStatus(statusUrl);
    if (lifecycleChangeStatus === 'success') {
      lifecycleTaskSuccess(benchlingApp.lifecycleState);
    } else if (lifecycleChangeStatus === 'failure') {
      lifecycleTaskFailure(benchlingApp.lifecycleState);
    } else {
      const currentTime = new Date().getTime();
      if (currentTime - pollingStartTime.current < LIFECYCLE_CHANGE_RESULT_MAXIMUM_POLLING_TIME) {
        const lifecycleTimeoutId = setTimeout(() => {
          pollForLifecycleChangeResult(statusUrl);
        }, LIFECYCLE_CHANGE_POLL_FREQUENCY);
        lifecycleTimeoutRef.current = lifecycleTimeoutId;
      } else if (lifecycleTimeoutRef.current) {
        clearTimeout(lifecycleTimeoutRef.current);
        lifecycleTaskTimeout(benchlingApp.lifecycleState);
      }
    }
  }

  return (
    <BenchlingAppLifecycleButton
      appId={id}
      appLifecycleState={benchlingApp.lifecycleState}
      pollForChangeComplete={pollForLifecycleChangeResult}
      pollingStartTime={pollingStartTime}
      name={name}
      appActivationViewable={appActivationViewable}
      refetch={lifecycleStateQueryResult.refetch}
      isLoading={false}
      missingWebhookUrl={!hasWebhookUrl}
    />
  );
}

const styles = StyleSheet.create({
  header: {
    height: '48px',
    lineHeight: '48px',
    padding: '0px 24px',
    borderBottom: `1px solid ${GRAY_LIGHT}`,
  },
  detailRow: {
    marginTop: '16px',
    padding: '0px 24px',
  },
  detailsGeneral: {
    paddingBottom: '16px',
  },
  canvasContainer: {
    margin: '0px 24px',
  },
  rightAlignedButton: {
    float: 'right',
    marginLeft: '7px',
  },
});
